import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, EnvelopeSimple, Gift, CheckCircle, Sparkle } from "@phosphor-icons/react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const NewsletterPopup = ({ trigger = "scroll", delay = 5000 }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    // Check if user already subscribed or dismissed
    const hasInteracted = localStorage.getItem("newsletter_popup_interacted");
    if (hasInteracted) return;

    if (trigger === "scroll") {
      const handleScroll = () => {
        const scrollPercent = (window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100;
        if (scrollPercent > 50) {
          setIsVisible(true);
          window.removeEventListener("scroll", handleScroll);
        }
      };
      window.addEventListener("scroll", handleScroll);
      return () => window.removeEventListener("scroll", handleScroll);
    } else if (trigger === "time") {
      const timer = setTimeout(() => setIsVisible(true), delay);
      return () => clearTimeout(timer);
    } else if (trigger === "exit") {
      const handleMouseLeave = (e) => {
        if (e.clientY <= 0) {
          setIsVisible(true);
          document.removeEventListener("mouseleave", handleMouseLeave);
        }
      };
      document.addEventListener("mouseleave", handleMouseLeave);
      return () => document.removeEventListener("mouseleave", handleMouseLeave);
    }
  }, [trigger, delay]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      await axios.post(`${API}/api/newsletter/subscribe`, {
        email,
        name,
        source: "popup"
      });
      setSubmitted(true);
      localStorage.setItem("newsletter_popup_interacted", "subscribed");
    } catch (err) {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setIsVisible(false);
    localStorage.setItem("newsletter_popup_interacted", "dismissed");
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100]"
          />
          
          {/* Popup */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: "spring", duration: 0.5 }}
            className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[101] w-full max-w-lg mx-4"
            data-testid="newsletter-popup"
          >
            <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
              {/* Header with gradient */}
              <div className="bg-gradient-to-r from-cyan-500 to-blue-600 p-8 text-white relative overflow-hidden">
                <motion.div
                  className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full"
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 4, repeat: Infinity }}
                />
                <motion.div
                  className="absolute -bottom-5 -left-5 w-24 h-24 bg-white/10 rounded-full"
                  animate={{ scale: [1.2, 1, 1.2] }}
                  transition={{ duration: 3, repeat: Infinity }}
                />
                
                <button
                  onClick={handleClose}
                  className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors"
                  data-testid="close-popup"
                >
                  <X weight="bold" className="w-4 h-4" />
                </button>
                
                <div className="relative">
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkle weight="fill" className="w-5 h-5 text-amber-300" />
                    <span className="text-sm font-semibold text-cyan-100">Exclusive Insights</span>
                  </div>
                  <h2 className="font-display text-2xl md:text-3xl font-bold mb-2">
                    Get Expert Hospice Compliance Tips
                  </h2>
                  <p className="text-cyan-100 font-medium">
                    Join 2,500+ hospice leaders receiving actionable insights weekly
                  </p>
                </div>
              </div>

              {/* Content */}
              <div className="p-8">
                {!submitted ? (
                  <>
                    {/* Benefits */}
                    <div className="mb-6 space-y-3">
                      {[
                        "Weekly compliance updates & regulatory changes",
                        "Exclusive templates & checklists",
                        "Early access to webinars & resources"
                      ].map((benefit, i) => (
                        <div key={i} className="flex items-center gap-3">
                          <div className="w-6 h-6 rounded-full bg-cyan-100 flex items-center justify-center flex-shrink-0">
                            <CheckCircle weight="fill" className="w-4 h-4 text-cyan-600" />
                          </div>
                          <span className="text-slate-700 font-medium text-sm">{benefit}</span>
                        </div>
                      ))}
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div>
                        <input
                          type="text"
                          placeholder="Your name"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          className="form-input"
                          data-testid="popup-name-input"
                        />
                      </div>
                      <div>
                        <input
                          type="email"
                          placeholder="your@email.com"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                          className="form-input"
                          data-testid="popup-email-input"
                        />
                      </div>
                      
                      {error && (
                        <p className="text-red-500 text-sm">{error}</p>
                      )}
                      
                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-bold py-4 rounded-xl hover:shadow-lg hover:shadow-cyan-500/30 transition-all flex items-center justify-center gap-2"
                        data-testid="popup-submit"
                      >
                        {loading ? (
                          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        ) : (
                          <>
                            <Gift weight="bold" className="w-5 h-5" />
                            Subscribe & Get Free Checklist
                          </>
                        )}
                      </button>
                    </form>

                    <p className="text-center text-slate-500 text-xs mt-4">
                      No spam, ever. Unsubscribe anytime.
                    </p>
                  </>
                ) : (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center py-6"
                  >
                    <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
                      <CheckCircle weight="fill" className="w-10 h-10 text-emerald-500" />
                    </div>
                    <h3 className="font-display text-2xl text-slate-900 mb-2">You're In!</h3>
                    <p className="text-slate-600 mb-6">
                      Check your email for your free CMS Survey Preparation Checklist.
                    </p>
                    <button
                      onClick={handleClose}
                      className="btn-primary"
                    >
                      Continue Reading
                    </button>
                  </motion.div>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default NewsletterPopup;
